import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { ChangeATMPINStackParamList, ChangeATMPINStackRoutes } from './type';
import { AccountManagementStackParamList, AccountManagementStackRoutes } from '../../account.navigator';
import { Card } from '../../../../../../packages/utx-access-client/src/react';
import { AlertType, UniAlert } from '@/design-system/components/alerts';

export interface ChangeATMPINProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  account: string;
  tab?: number;
  record?: Card;
  func?: (cardId: string, pin: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
}

export const ChangeATMPINWebOneScreen: React.FC<ChangeATMPINProps> = (props) => {
  const { onClose, onLayout, data, account, tab, record, func, onReturnData } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { body, textMb, textTitle, textAtmPin, btnView, btn } = sxStyles;
  const [loading, setLoading] = useState(false);
  const showPassword = (atmPin: string) => {
    return `${atmPin.substring(0, 1)}****${atmPin.substring(atmPin.length - 1)}`;
  };

  const navigation = useNavigation<NativeStackNavigationProp<ChangeATMPINStackParamList>>();
  const accountNavigation = useNavigation<NativeStackNavigationProp<AccountManagementStackParamList>>();

  const [alertMessage, setAlertMessage] = useState('');

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader
        title={t('card-account-management::change-atm-pin.title--change-atm-pin-confirmation')}
        closeModal={() => navigation.getParent()?.goBack()}
      />
      <View sx={body}>
        <TextBody sx={textMb} semiBold>
          {t('card-account-management::change-atm-pin.text--change-atm-pin-inquiry')}
        </TextBody>
        <TextCaption sx={textTitle} semiBold>
          {t('card-account-management::change-atm-pin.text--new-atm-pin')}
        </TextCaption>
        <H6 sx={textAtmPin}>{showPassword(data.pin)}</H6>
        {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
        <View sx={btnView}>
          <UniButton
            sx={btn}
            disabled={loading}
            variant={ButtonVariant.outline1}
            label={t('card-account-management::general.button--cancel')}
            onPress={() => navigation.getParent()?.goBack()}
          />
          <UniButton
            loading={loading}
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::general.button--place-instruction')}
            onPress={() => {
              if (func) {
                setLoading(true);
                func(
                  record?.id!,
                  data.pin,
                  (returnData) => {
                    setLoading(false);
                    onReturnData && onReturnData(returnData.data);
                    navigation.navigate(ChangeATMPINStackRoutes.Placed, {
                      onLayout: onLayout,
                      data,
                      account,
                      tab,
                      returnData: returnData.data
                    });
                  },
                  (error) => {
                    setLoading(false);
                    setAlertMessage(error?.message);
                  }
                );
              }
            }}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40
  },
  btn: {
    width: 216,
    height: 44
  }
};
